import {
  MAX_CREATIVES_PER_BATCH,
  MAX_UPLOAD_BYTES,
  UPLOAD_CONTENT_TYPES,
} from '@alison/api/uploads';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import CloudUploadOutlined from '@mui/icons-material/CloudUploadOutlined';
import ErrorOutlineOutlined from '@mui/icons-material/ErrorOutlineOutlined';
import InsertDriveFileOutlined from '@mui/icons-material/InsertDriveFileOutlined';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { type DragEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { CreativeName } from './CreativeName.js';
import { formatBytes, type RejectedFile, rowLine, type UploadRow } from './uploadRows.js';

const ACCEPTED = UPLOAD_CONTENT_TYPES.join(',');
// en-GB: no Oxford comma, so this reads "MP4, PNG or JPEG" like the rest of the copy.
const TYPE_LABELS = new Intl.ListFormat('en-GB', { type: 'disjunction' }).format(
  UPLOAD_CONTENT_TYPES.map((contentType) => contentType.split('/')[1]?.toUpperCase() ?? ''),
);
const STATUS_ICON_SX = { fontSize: 18 };

const DROPZONE_SX = {
  gap: 1,
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 180,
  px: 4,
  py: 8,
  borderRadius: 1,
  border: '1px dashed',
  borderColor: 'border.dashed',
  bgcolor: 'background.page',
  cursor: 'pointer',
  transition: 'border-color 120ms, background-color 120ms',
  position: 'relative',
};

const INVITED_SX = { borderColor: 'primary.main', bgcolor: 'info.bg' };

// Once files land, the list (and each row's check) must stay above the fold.
const COMPACT_SX = { minHeight: 0, py: 3 };

// Not `display: none`, which takes the input out of the tab order and the
// accessibility tree, leaving no keyboard path to the picker.
const HIDDEN_INPUT_STYLE = {
  position: 'absolute' as const,
  inset: 0,
  width: '100%',
  height: '100%',
  opacity: 0,
  cursor: 'pointer',
};

const ROW_SX = {
  gap: 2,
  alignItems: 'center',
  px: 3,
  py: 2,
  borderRadius: 1,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
};

interface UploadStepProps {
  rows: UploadRow[];
  rejected: RejectedFile[];
  onFilesPicked: (files: File[]) => void;
  onRemove: (key: string) => void;
  onRename: (key: string, fileName: string) => void;
}

function RowStatus({ row }: { row: UploadRow }): ReactNode {
  if (row.status === 'done') {
    return (
      <CheckCircleOutlined
        aria-label="Uploaded"
        sx={{ ...STATUS_ICON_SX, color: 'success.main' }}
      />
    );
  }
  if (row.status === 'failed') {
    return (
      <ErrorOutlineOutlined aria-label="Failed" sx={{ ...STATUS_ICON_SX, color: 'error.main' }} />
    );
  }
  if (row.status === 'uploading') {
    return <CircularProgress aria-label="Uploading" size={16} />;
  }
  return <InsertDriveFileOutlined aria-label="Waiting" sx={STATUS_ICON_SX} />;
}

export function UploadStep({
  rows,
  rejected,
  onFilesPicked,
  onRemove,
  onRename,
}: UploadStepProps): ReactNode {
  const [inviting, setInviting] = useState(false);
  const hasRoom = rows.length < MAX_CREATIVES_PER_BATCH;
  const listRef = useRef<HTMLDivElement>(null);
  const shownCount = useRef(rows.length);

  // Rows land below the dropzone; bring the newest one's status into view when files are added.
  useEffect(() => {
    if (rows.length > shownCount.current)
      listRef.current?.lastElementChild?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    shownCount.current = rows.length;
  }, [rows.length]);

  function pick(files: FileList | null): void {
    if (files !== null && files.length > 0) onFilesPicked([...files]);
  }

  function onDrop(event: DragEvent<HTMLElement>): void {
    event.preventDefault();
    event.stopPropagation();
    setInviting(false);
    pick(event.dataTransfer.files);
  }

  // A dragleave also fires when the cursor crosses onto a child, which would
  // flicker the invite; only a cursor outside the zone has really left.
  function onDragLeave(event: DragEvent<HTMLElement>): void {
    event.preventDefault();
    event.stopPropagation();
    const zone = event.currentTarget.getBoundingClientRect();
    const gone =
      event.clientX < zone.left ||
      event.clientX > zone.right ||
      event.clientY < zone.top ||
      event.clientY > zone.bottom;
    if (gone) setInviting(false);
  }

  return (
    <Stack sx={{ gap: 5 }}>
      <Stack
        data-testid="upload-dropzone"
        onDrop={onDrop}
        onDragEnter={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (hasRoom) setInviting(true);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
        onDragLeave={onDragLeave}
        sx={[DROPZONE_SX, rows.length > 0 && COMPACT_SX, inviting && INVITED_SX]}
      >
        <CloudUploadOutlined
          sx={{ fontSize: 28, color: inviting ? 'primary.main' : 'text.disabled' }}
        />
        {inviting ? (
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: 'primary.main' }}>
            Drop files here...
          </Typography>
        ) : (
          <>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
              Drag &amp; drop your creatives here
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              or click to browse — {TYPE_LABELS}, up to {formatBytes(MAX_UPLOAD_BYTES)} each
            </Typography>
          </>
        )}
        <input
          type="file"
          multiple
          accept={ACCEPTED}
          aria-label="Add creatives"
          onChange={(event) => {
            pick(event.currentTarget.files);
            event.currentTarget.value = '';
          }}
          style={HIDDEN_INPUT_STYLE}
        />
      </Stack>

      {rejected.length === 0 ? null : (
        <Stack sx={{ gap: 1 }}>
          {rejected.map((entry) => (
            <Alert key={`${entry.fileName}:${entry.reason}`} severity="error">
              {entry.fileName} — {entry.reason}
            </Alert>
          ))}
        </Stack>
      )}

      {rows.length === 0 ? null : (
        <Stack ref={listRef} sx={{ gap: 2 }}>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            {rows.length} of {MAX_CREATIVES_PER_BATCH}
          </Typography>
          {rows.map((row) => (
            <Stack key={row.key} direction="row" data-testid={`upload-row-${row.key}`} sx={ROW_SX}>
              <RowStatus row={row} />
              <Stack sx={{ gap: 0.25, minWidth: 0, flex: 1 }}>
                <CreativeName
                  name={row.fileName}
                  editLabel={`Rename ${row.fileName}`}
                  nameSx={{ fontSize: 13, fontWeight: 600 }}
                  fullWidth
                  onRename={(name) => onRename(row.key, name)}
                />
                {row.status === 'failed' ? (
                  <Alert severity="error">{rowLine(row)}</Alert>
                ) : (
                  <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                    {rowLine(row)}
                  </Typography>
                )}
              </Stack>
              <IconButton
                size="small"
                aria-label={`Remove ${row.fileName}`}
                onClick={() => onRemove(row.key)}
              >
                <CloseOutlined sx={{ fontSize: 16 }} />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
